import {
  Component,
  HostListener,
  Input,
  OnInit,
  OnDestroy,
  computed,
} from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { Router } from '@angular/router';
import { debounceTime, finalize, of, Subscription, switchMap, tap } from 'rxjs';
import { ColDef, GridApi, GridReadyEvent } from 'ag-grid-community';
import { FormsManagementInventoryService } from 'src/app/services/forms-management-inventory.service';
import {
  CustomFieldDefinitionsService,
  CustomFieldDefinition,
} from 'src/app/services/custom-field-definitions.service';
import { DeleteConfirmationDialogService } from 'src/app/services/delete-confirmation-dialog.service';
import { ToastService } from 'src/app/services/toast.service';
import { UsersService } from 'src/app/services/users.service';
import { Role } from 'src/app/models/user/role.model';
import { toSignal } from '@angular/core/rxjs-interop';
import { AgGridWrapperComponent } from '../ag-grid-wrapper/ag-grid-wrapper.component';
import { SearchSortComponent } from '../search-sort/search-sort.component';
import { BtnCellRenderer } from '../btn-cell-renderer/btn-cell-renderer.component';
import { TextTooltipRenderer } from '../text-tooltip-cell-renderer/text-tooltip-cell-renderer.component';
import { baseColDef } from '../../utils';
import { FormStatusCellRenderer } from '../form-status/form-status-cell-renderer.component';
import { MultiBadgeCellRenderer } from '../multi-badge-cell-renderer/multi-badge-cell-renderer.component';
import { GridDocumentComponent } from '../grid-document/grid-document.component';
import { FormSubmissionTransferService } from '../../views/form-submission/form-submission-transfer.service';
import { CustomColumnHeaderComponent } from '../custom-column-header/custom-column-header.component';
import { ProviderOrganizationsService } from 'src/app/services/provider.organizations.service';
import { MatMenuModule } from '@angular/material/menu';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatSlideToggle } from '@angular/material/slide-toggle';

@Component({
  selector: 'app-forms-inventory-panel',
  templateUrl: './forms-inventory-panel.component.html',
  styleUrls: ['./forms-inventory-panel.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatIconModule,
    MatPaginatorModule,
    AgGridWrapperComponent,
    SearchSortComponent,
    CustomColumnHeaderComponent,
    MatMenuModule,
    MatCheckboxModule,
    MatSlideToggle,
  ],
})
export class FormsInventoryPanelComponent implements OnInit, OnDestroy {
  @Input() organizationId!: number;
  @Input() isLenderView = false;

  isLoading = false;
  datePipe = new DatePipe('en-US');
  subscriptions: Subscription[] = [];

  pageSettings = {
    defaultPageSize: 20,
    pageSizeOptions: [20, 50, 100],
  };

  currentPage = 0;
  currentCursorId: number | null = null;
  currentPageSize: number = 20;
  pageSize = this.pageSettings.defaultPageSize;
  totalCount = 0;
  searchKey = '';

  searchControl: FormControl = new FormControl('');
  currentSortField = { sortValue: 'id' };
  currentIsAscending = false;

  sortForm: FormGroup = new FormGroup({
    sortField: new FormControl({ sortValue: this.currentSortField.sortValue }),
    isAscending: new FormControl(this.currentIsAscending),
  });

  sortOptions = [
    { label: 'Form No', sortValue: 'formno' },
    { label: 'Form Name', sortValue: 'formname' },
    { label: 'Provider', sortValue: 'provider' },
    { label: 'Administrator', sortValue: 'administrator' },
  ];

  // Static base columns — custom and action columns are injected separately in rebuildColumnDefs()
  private readonly baseColumnDefs: ColDef[] = [
    {
      ...baseColDef,
      field: 'formNo',
      headerName: 'Form No',
      width: 150,
      sortable: true,
      cellRenderer: TextTooltipRenderer,
      cellRendererParams: {
        onClick: this.viewFormDetail.bind(this),
      },
    },
    {
      ...baseColDef,
      field: 'intendedDateOfUse',
      headerName: 'Revision Date',
      width: 150,
      cellRenderer: (data: { value: string | null }) => {
        return data.value ? new Date(data.value).toLocaleDateString() : '-';
      },
    },
    {
      ...baseColDef,
      field: 'formName',
      headerName: 'Form Name',
      width: 150,
      sortable: true,
      cellRenderer: TextTooltipRenderer,
    },
    {
      ...baseColDef,
      field: 'providerName',
      headerName: 'Provider',
      width: 150,
      sortable: true,
      cellRenderer: TextTooltipRenderer,
      valueGetter: (params: any) =>
        params.data?.providerOrganization?.name ||
        params.data?.provider?.name ||
        '-',
    },
    {
      ...baseColDef,
      field: 'administratorName',
      headerName: 'Administrator',
      width: 150,
      sortable: true,
      cellRenderer: TextTooltipRenderer,
      valueGetter: (params: any) => params.data?.administrator?.name || '-',
    },
    {
      ...baseColDef,
      field: 'industryName',
      headerName: 'Industry',
      width: 150,
      sortable: false,
      cellRenderer: TextTooltipRenderer,
      valueGetter: (params: any) =>
        params.data?.industries?.map((i: any) => i.name).join(', ') || '-',
    },
    {
      ...baseColDef,
      field: 'products',
      headerName: 'Products',
      cellRenderer: MultiBadgeCellRenderer,
      width: 200,
      valueGetter: (params: any) => params.data?.products || [],
    },
    {
      ...baseColDef,
      field: 'states',
      headerName: 'States',
      cellRenderer: MultiBadgeCellRenderer,
      cellRendererParams: {
        maxItems: 3,
        separator: ' - ',
      },
      width: 200,
      valueGetter: (params: any) => params.data?.states || [],
    },
    {
      ...baseColDef,
      field: 'lenderStatuses',
      headerName: 'Lender Status',
      cellRenderer: FormStatusCellRenderer,
      width: 200,
      valueGetter: (params: any) => {
        return params.data?.lenderStatuses || [];
      },
    },
    {
      ...baseColDef,
      field: 'additionalLenderApprovals',
      headerName: 'Additional Lender Approvals',
      cellRenderer: TextTooltipRenderer,
      width: 200,
      valueGetter: (params: any) =>
        params.data?.additionalLenderApprovals || '',
    },
    {
      ...baseColDef,
      field: 'insuranceCompanyName',
      headerName: 'Insurance Company Name',
      cellRenderer: TextTooltipRenderer,
      width: 200,
      valueGetter: (params: any) =>
        params.data?.insuranceCompanyName || '',
    },
    {
      ...baseColDef,
      field: 'documents',
      headerName: 'Product Form',
      width: 300,
      cellRenderer: GridDocumentComponent,
      valueGetter: (params: any) => {
        // Transform documents to have serverFileName field (backend returns 'url')
        const documents = params.data?.documents || [];
        return documents.map((doc: any) => ({
          ...doc,
          serverFileName: doc.url || doc.serverFileName || doc.name,
        }));
      },
    },
    {
      ...baseColDef,
      field: 'formStatus',
      headerName: 'Form Status',
      width: 120,
      valueGetter: (params: any) => {
        const status = params.data?.formStatus || 1; // Default to Active (1)
        return status === 1
          ? { statusId: 5, name: 'Active' }
          : {
              statusId: 6,
              name: 'Inactive',
              dateMarkedInactive: params.data?.dateMarkedInactive,
              inactivatedByUser: params.data?.inactivatedByUser,
            };
      },
      cellRenderer: FormStatusCellRenderer,
      comparator: (a, b) => (a?.statusId ?? 0) - (b?.statusId ?? 0),
    },
    {
      ...baseColDef,
      field: 'notes',
      headerName: 'Notes',
      width: 200,
      cellRenderer: TextTooltipRenderer,
      valueGetter: (params) => {
        return params.data.notes || '';
      },
    },
  ];

  // Kept for template binding (ag-grid-wrapper expects [columnDefs])
  public columnDefs: ColDef[] = [...this.baseColumnDefs];
  public rowData: any[] = [];
  gridApi!: GridApi;

  // Dynamic custom columns state
  customColumns: CustomFieldDefinition[] = [];
  canAddColumns = false;

  private readonly ENTITY_TYPE = 'FormsManagementInventory';

  readonly userRole = toSignal(this.userService.userRole$, {
    initialValue: undefined,
  });
  readonly isAdmin = computed(() => this.userRole() === Role.Admin);
  readonly isProvider = computed(() => this.userRole() === Role.Provider);
  readonly isSystem = computed(() => this.userRole() === Role.SystemUser);
  readonly isConsultant = computed(() => this.userRole() === Role.Consultant);
  readonly isLender = computed(() => this.userRole() === Role.Lender);
  readonly isAdminOrSystem = computed(() => {
    const role = this.userRole();
    return role === Role.Admin || role === Role.SystemUser;
  });
  readonly canCreateFormManagement = computed(() => {
    const role = this.userRole();
    return role !== Role.Lender && role !== Role.ProviderReadOnly && role !== Role.LenderReadOnly;
  });
  readonly canSubmitForm = computed(() => {
    const role = this.userRole();
    return role !== Role.Lender && role !== Role.ProviderReadOnly && role !== Role.LenderReadOnly;
  });

  constructor(
    private readonly formsInventoryService: FormsManagementInventoryService,
    private readonly customFieldsService: CustomFieldDefinitionsService,
    private readonly deleteDialog: DeleteConfirmationDialogService,
    private readonly toastService: ToastService,
    private readonly userService: UsersService,
    private readonly transferService: FormSubmissionTransferService,
    private readonly providerOrgsService: ProviderOrganizationsService,
    private readonly router: Router,
  ) {}

  ngOnInit(): void {
    const searchSubscription = this.searchControl.valueChanges
      .pipe(
        debounceTime(300),
        tap(() => {
          this.currentPage = 0;
          this.currentCursorId = null;
          this.reload();
        }),
      )
      .subscribe();

    this.subscriptions.push(searchSubscription);
  }

  ngOnDestroy(): void {
    for (const s of this.subscriptions) {
      s.unsubscribe();
    }
  }

  onGridReady(params: GridReadyEvent): void {
    this.gridApi = params.api;
    this.reload();
    this.loadCustomColumns();
  }

  visibleCustomColumns = this.customColumns
    .filter((x) => x.isVisibleInGrid)
    .sort((a, b) => a.sortOrder - b.sortOrder);

  // --- Custom column methods ---

  private loadCustomColumns(): void {
    const canPotentiallyAdd =
      this.isAdmin() || this.isProvider() || this.isConsultant();

    if (!canPotentiallyAdd) {
      this.canAddColumns = false;
      this.fetchAndRenderColumns();
      return;
    }

    // Only parent/standalone orgs can manage columns — child orgs inherit parent's columns
    this.providerOrgsService
      .getRelationships(this.organizationId.toString())
      .subscribe((response) => {
        const isChildOrg = !response.isError && response.data?.parent != null;
        this.canAddColumns = !isChildOrg;
        this.fetchAndRenderColumns();
      });
  }

  private fetchAndRenderColumns(): void {
    this.customFieldsService
      .getDefinitions(this.organizationId, this.ENTITY_TYPE)
      .subscribe((response) => {
        this.customColumns =
          !response.isError && response.data ? response.data : [];
        this.rebuildColumnDefs();
      });
  }
  onAddColumn(name: string, dataType: string, options?: any): void {
    if (this.customColumns.length >= 3) return;

    this.customFieldsService
      .createDefinition({
        orgId: this.organizationId,
        entityType: this.ENTITY_TYPE,
        fieldLabel: name,
        dataType: dataType || 'text',
        isRequired: options?.isRequired ?? false,
        isVisibleInGrid: options?.isVisibleInGrid ?? true,
        isVisibleInForm: options?.isVisibleInForm ?? true,
        sortOrder: this.customColumns.length,
      })
      .subscribe((response) => {
        if (!response.isError && response.data) {
          this.customColumns.push(response.data);
          this.rebuildColumnDefs();
        }
      });
  }

  toggleCustomColumnGrid(column: any): void {
    this.updateCustomColumnVisibility(column, {
      isVisibleInGrid: !column.isVisibleInGrid,
    });
  }

  toggleCustomColumnForm(column: any): void {
    this.updateCustomColumnVisibility(column, {
      isVisibleInForm: !column.isVisibleInForm,
    });
  }

  private updateCustomColumnVisibility(column: any, changes: any): void {
    const updated = {
      ...column,
      ...changes,
    };

    this.customFieldsService
      .updateDefinition(column.id, updated)
      .subscribe((response) => {
        if (!response.isError) {
          Object.assign(column, changes);
          this.rebuildColumnDefs();
        } else {
          this.toastService.error('Error', 'Failed to update custom column');
        }
      });
  }
  onRenameColumn(columnId: number, name: string): void {
    const col = this.customColumns.find((c) => c.id === columnId);
    if (!col) return;
    this.customFieldsService
      .updateDefinition(columnId, {
        fieldLabel: name,
        dataType: col.dataType,
        isRequired: col.isRequired,
        isVisibleInGrid: col.isVisibleInGrid,
        isVisibleInForm: col.isVisibleInForm,
        sortOrder: col.sortOrder,
      })
      .subscribe((response) => {
        if (!response.isError && response.data) {
          col.fieldLabel = response.data.fieldLabel;
          this.rebuildColumnDefs();
        } else {
          this.toastService.error('Error', 'Failed to rename column');
        }
      });
  }

  onDeleteColumn(columnId: number): void {
    this.deleteDialog
      .openDeleteConfirmation(
        'All existing values for this column will no longer be displayed.',
        'Delete column',
        'Delete',
      )
      .afterClosed()
      .subscribe((confirmed) => {
        if (!confirmed) return;
        this.customFieldsService
          .deleteDefinition(columnId)
          .subscribe((response) => {
            if (!response.isError) {
              this.customColumns = this.customColumns.filter(
                (c) => c.id !== columnId,
              );
              this.rebuildColumnDefs();
            } else {
              this.toastService.error('Error', 'Failed to delete column');
            }
          });
      });
  }

  // --- Column building ---

  private buildCustomColDefs(): ColDef[] {
    const visibleCustomColumns = this.customColumns
      .filter((col) => col.isVisibleInGrid)
      .sort((a, b) => a.sortOrder - b.sortOrder);

    const defs: ColDef[] = visibleCustomColumns.map((col) => ({
      ...baseColDef,
      field: `customFields.${col.fieldKey}`,
      width: 200,
      sortable: false,
      cellClass: 'custom-field-cell',
      headerComponent: CustomColumnHeaderComponent,
      headerComponentParams: {
        mode: 'rename',
        columnName: col.fieldLabel,
        columnId: col.id,
        canRename: this.canAddColumns,
        onRename: this.onRenameColumn.bind(this),
        onDelete: this.canAddColumns
          ? this.onDeleteColumn.bind(this)
          : undefined,
      },
      valueGetter: (params: any) =>
        params.data?.customFields?.[col.fieldKey] ?? '',
      valueFormatter: (params: any) => {
        if (col.dataType !== 'date' || !params.value) {
          return params.value ?? '';
        }

        return new Date(params.value).toLocaleDateString();
      },
    }));

    if (this.canAddColumns && this.customColumns.length < 3) {
      defs.push({
        field: 'customColAdd',
        headerName: '',
        width: 150,
        minWidth: 100,
        maxWidth: 200,
        sortable: false,
        filter: false,
        resizable: false,
        cellClass: 'custom-field-cell',
        headerComponent: CustomColumnHeaderComponent,
        headerComponentParams: {
          mode: 'add',
          canAdd: true,
          onAdd: (name: string, dataType: string, options?: any) =>
            this.onAddColumn(name, dataType, options),
        },
      });
    }

    return defs;
  }

  private buildActionCol(): ColDef {
    const canManage = this.canCreateFormManagement();
    const canSubmit = this.canSubmitForm();
    return {
      field: 'action',
      minWidth: 160,
      maxWidth: 160,
      filter: false,
      sortable: false,
      cellRenderer: BtnCellRenderer,
      cellRendererParams: {
        // Delete: Only if user has permission
        onDelete: (data: any) => (!canManage ? null : this.onDelete(data)),

        // Submit: Only if not submitted AND user has permission (includes Providers)
        onSubmit: (data: any) =>
          data.isSubmitted || !canSubmit ? null : this.onSubmit(data),
        // Toggle Status: Only Admin
        onToggleStatus: this.isAdmin()
          ? (data: any) => this.onToggleFormStatus(data)
          : null,
        showToggleStatus: this.isAdmin(),
      },
    };
  }

  private rebuildColumnDefs(): void {
    const allCols: ColDef[] = [
      ...this.baseColumnDefs,
      ...this.buildCustomColDefs(),
    ];

    if (!this.isLenderView && this.userRole() !== Role.ProviderReadOnly && this.userRole() !== Role.LenderReadOnly) {
      allCols.push(this.buildActionCol());
    }
    this.columnDefs = allCols;

    this.gridApi.updateGridOptions({ columnDefs: allCols });
  }

  onToggleFormStatus(data: any): void {
    const newStatus = data.formStatus === 1 ? 'Inactive' : 'Active';

    this.deleteDialog
      .openDeleteConfirmation(
        `Are you sure you want to change the form status to ${newStatus}?`,
        'Toggle Form Status',
        'Confirm',
      )
      .afterClosed()
      .pipe(
        switchMap((confirmed) => {
          if (confirmed) {
            return this.formsInventoryService.toggleFormStatus(data.id);
          }
          return of(null);
        }),
      )
      .subscribe({
        next: (result: any) => {
          if (result && !result.isError) {
            this.toastService.success(
              'Success',
              `Form status changed to ${result.data.formStatus}`,
            );
            this.reload();
          }
        },
        error: (err) => {
        },
      });
  }

  navigateToBulkUpload(): void {
    const userRole = this.userRole();
    let route: string;

    if (userRole === Role.Admin) {
      route = '/admin/bulk-upload';
    } else if (userRole === Role.Provider || userRole === Role.Consultant) {
      route = '/provider/bulk-upload';
    } else {
      route = '/admin/bulk-upload';
    }

    this.router.navigate([route], {
      queryParams: {
        from: 'forms-management',
        organizationId: this.organizationId,
      },
    });
  }

  createNewFormsManagement(): void {
    const userRole = this.userRole();
    let route: string;

    if (userRole === Role.Admin) {
      route = '/admin/forms-management/create';
    } else if (userRole === Role.Provider || userRole === Role.Consultant) {
      route = '/provider/forms-management/create';
    } else {
      // Default to admin route for system users
      route = '/admin/forms-management/create';
    }

    // Use queryParams instead of state
    this.router.navigate([route], {
      queryParams: { organizationId: this.organizationId },
    });
  }

  onDelete(data: any): void {
    this.deleteDialog
      .openDeleteConfirmation(
        'This form will be permanently deleted from your inventory.',
        'Delete confirmation',
        'Delete',
      )
      .afterClosed()
      .pipe(
        switchMap((confirmed) => {
          if (confirmed) {
            return this.formsInventoryService.deleteForm(data.id);
          }
          return of(null);
        }),
      )
      .subscribe({
        next: (result: any) => {
          if (result && !result.isError) {
            this.toastService.success('Success', 'Form deleted successfully');
            this.reload();
          } else if (result === null) {
          } else {
          }
        },
        error: (err) => {
          console.error('Delete error:', err);
          this.toastService.success('Error', 'Failed to delete form');
        },
      });
  }

  viewFormDetail(data: any): void {
    // Navigate to the inventory details view with organization ID for back navigation
    const userRole = this.userRole();
    let route: string;

    if (userRole === Role.Admin) {
      route = '/admin/forms-management/details';
    } else if (userRole === Role.Provider || userRole === Role.Consultant || userRole === Role.ProviderReadOnly) {
      route = '/provider/forms-management/details';
    } else if (userRole === Role.Lender || userRole === Role.LenderReadOnly) {
      route = '/lender/forms-management/details';
    } else {
      route = '/admin/forms-management/details';
    }

    // Pass organization ID in navigation state for back navigation
    this.router.navigate([`${route}/${data.id}`], {
      state: { organizationId: this.organizationId },
    });
  }

  onSubmit(data: any): void {
    this.deleteDialog
      .openDeleteConfirmation('', 'Create Form Submission', 'Create')
      .afterClosed()
      .subscribe((confirmed) => {
        if (confirmed) {
          // Fetch full form details including cancellation data before navigating
          this.isLoading = true;
          this.formsInventoryService.getById(data.id.toString()).subscribe({
            next: (response) => {
              this.isLoading = false;
              if (!response.isError && response.data) {
                this.navigateToFormSubmission(response.data);
              } else {
                console.error('Failed to fetch form details');
                this.toastService.success(
                  'Error',
                  'Failed to load form details',
                );
              }
            },
            error: (err) => {
              this.isLoading = false;
              console.error('Error fetching form details:', err);
              this.toastService.success('Error', 'Failed to load form details');
            },
          });
        }
      });
  }

  private navigateToFormSubmission(inventoryData: any): void {
    // Determine the correct route based on user role
    const userRole = this.userRole();
    let route: string;

    if (userRole === Role.Admin) {
      route = '/admin/form-submission/create';
    } else if (userRole === Role.Provider || userRole === Role.Consultant) {
      route = '/provider/form-submission/create';
    } else {
      // Default to admin route for system users
      route = '/admin/form-submission/create';
    }
    const formProviderOrgId =
      inventoryData.providerOrganization?.id ||
      inventoryData.providerId ||
      inventoryData.providerOrganizationId ||
      inventoryData.provider?.id ||
      this.organizationId;
    let providerObj = inventoryData.providerOrganization
      ? {
          id: Number(inventoryData.providerOrganization.id),
          name: inventoryData.providerOrganization.name,
        }
      : null;
    if (!providerObj && inventoryData.provider) {
      providerObj = {
        id: Number(formProviderOrgId),
        name: inventoryData.provider.name,
      };
    }
    if (!providerObj) {
      providerObj = { id: Number(formProviderOrgId), name: 'Provider' };
    }

    const transferData = {
      fromInventory: true,
      fromFormsManagement: false,
      organizationId: formProviderOrgId,
      inventoryFormId: inventoryData.id,
      prepopulatedData: {
        formNo: inventoryData.formNo,
        formName: inventoryData.formName,
        previousApprovedFormNo: inventoryData.previousApprovedFormNo,
        intendedDateOfUse: inventoryData.intendedDateOfUse,
        dueDate: inventoryData.dueDate,
        // Pass both ID and full object for dropdowns (ensure IDs are numbers)
        providerId: Number(formProviderOrgId),
        provider: providerObj,
        administratorId:
          inventoryData.administrator?.id || inventoryData.administratorId,
        administrator: inventoryData.administrator,
        industryIds:
          inventoryData.industries?.map((i: any) => i.id) ||
          inventoryData.industryIds ||
          [],
        industries: inventoryData.industries || [],
        // Always set to "New Submission" (ID = 1) when coming from Forms Management
        submissionTypeId: 1,
        submissionType: { id: 1, name: 'New Submission' },
        additionalProviders: inventoryData.additionalProviders,
        additionalAdministrators: inventoryData.additionalAdministrators,
        notes: inventoryData.notes,
        additionalLenderApprovals: inventoryData.additionalLenderApprovals,
        insuranceCompanyName: inventoryData.insuranceCompanyName,
        // Pass arrays of full objects for multi-selects
        lenderIds:
          inventoryData.lenderStatuses?.map((l: any) => l.id) ||
          inventoryData.lenderIds ||
          [],
        lenders: inventoryData.lenderStatuses || [],
        stateIds:
          inventoryData.states?.map((s: any) => s.id) ||
          inventoryData.stateIds ||
          [],
        states: inventoryData.states || [],
        productIds:
          inventoryData.products?.map((p: any) => p.id) ||
          inventoryData.productIds ||
          [],
        products: inventoryData.products || [],
        marketingMaterialIds: inventoryData.marketingMaterialIds || [],
        marketingMaterials: inventoryData.marketingMaterials || [],
        documents: inventoryData.documents || [],
        // Include cancellation data from forms management
        cancellability: inventoryData.cancellability,
        cancellationFees: inventoryData.cancellationFees,
        refundMethodology: inventoryData.refundMethodology,
        formsManagementInventoryId: inventoryData.id,
      },
    };

    this.transferService.setData(transferData);
    this.router.navigate([route]);
  }

  private reload(backward: boolean = false): void {
    this.isLoading = true;
    this.searchKey = this.searchControl.value?.toLowerCase() || '';

    if (this.gridApi) {
      this.gridApi.showLoadingOverlay();
    }

    let params: any = {
      sortColumn: this.currentSortField.sortValue,
      sortOrder: this.currentIsAscending ? 'asc' : 'desc',
      forward: !backward,
      pageSize: this.pageSize,
      searchKey: this.searchKey,
    };
    if (this.currentCursorId) {
      params = { ...params, lastId: this.currentCursorId };
    }

    const subscription = this.formsInventoryService
      .getByOrganizationId(this.organizationId, params)
      .pipe(
        switchMap((response: any) => {
          if (response.isError) {
            return of({ items: [], totalCount: 0 });
          }
          return of(response.data);
        }),
        finalize(() => {
          this.isLoading = false;
        }),
      )
      .subscribe((data) => {
        if (this.gridApi) {
          this.gridApi.hideOverlay();
        }
        this.rowData = data.items || [];
        this.totalCount = data.totalCount || 0;
      });

    this.subscriptions.push(subscription);
  }

  setSortField(): void {
    const { sortField, isAscending } = this.sortForm.value;
    this.currentIsAscending = isAscending;
    this.currentSortField = sortField;
    this.currentCursorId = null;
    this.currentPage = 0;
    this.reload();
  }

  resetSortField(): void {
    this.sortForm.setValue({
      sortField: this.currentSortField,
      isAscending: this.currentIsAscending,
    });
  }

  handlePageEvent(event: PageEvent): void {
    if (this.isLoading) return;

    let backward = false;
    if (this.currentPageSize !== event.pageSize) {
      this.currentCursorId = null;
      this.currentPageSize = event.pageSize;
    } else if (
      !event.previousPageIndex ||
      event.previousPageIndex < event.pageIndex
    ) {
      this.currentCursorId =
        this.rowData.length > 0
          ? this.rowData[this.rowData.length - 1].id
          : null;
    } else {
      this.currentCursorId = this.rowData[0].id;
      backward = true;
    }

    this.currentPage = event.pageIndex;
    this.pageSize = event.pageSize;
    this.reload(backward);
  }
}
